import React from 'react';
import { Form, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Plus, Trash2, Check, X, HelpCircle } from 'lucide-react';
import { BackupSource, BackupSourceStatus, BackupSourceType } from '../../../../models/BackupSource';
import { OFFICIAL_STORE_PREFERRED_VALUE, parseSourcingBackupLinks, serializeSourcingBackupLinks } from '../../../../utils/sourcingLinks';

interface SourcingBackupLinksEditorProps {
  backupSourcesJson?: string | undefined;
  preferredSource?: string | undefined;
  mainCoupangUrl?: string | undefined;
  hasOfficialStoreUrl: boolean;
  onBackupSourcesChange: (serializedBackupSources: string) => void;
  onPreferredSourceChange: (preferredSource: string) => void;
}

// Backups are the scrapable channels only; official store is captured separately for bookkeeping.
const BACKUP_SOURCE_TYPES: BackupSourceType[] = [BackupSourceType.COUPANG, BackupSourceType.NAVER];

const SOURCE_LABELS: Record<string, string> = {
  [BackupSourceType.COUPANG]: 'Coupang',
  [BackupSourceType.NAVER]: 'Naver',
  [OFFICIAL_STORE_PREFERRED_VALUE]: 'Official Store',
};

// Monochrome status indicator: check = in stock, cross = not in stock, question = not yet scraped.
// obsolete/error fold into the cross (not available to sell). Tooltip carries the in/out/not-scraped meaning.
const renderBackupStatus = (status: BackupSourceStatus | null | undefined, backupIndex: number) => {
  let statusIcon: React.ReactNode;
  let statusLabel: string;
  if (status === 'instock') {
    statusIcon = <Check size={16} />;
    statusLabel = 'In Stock';
  } else if (status === 'outofstock' || status === 'obsolete' || status === 'error') {
    statusIcon = <X size={16} />;
    statusLabel = 'Out of Stock';
  } else {
    statusIcon = <HelpCircle size={16} />;
    statusLabel = 'Not Scraped';
  }
  return (
    <OverlayTrigger placement="top" delay={{ show: 150, hide: 0 }} overlay={<Tooltip id={`backup-status-${backupIndex}`}>{statusLabel}</Tooltip>}>
      <span className="d-inline-flex" style={{ cursor: 'help' }}>
        {statusIcon}
      </span>
    </OverlayTrigger>
  );
};

export const SourcingBackupLinksEditor: React.FC<SourcingBackupLinksEditorProps> = ({
  backupSourcesJson,
  preferredSource,
  mainCoupangUrl,
  hasOfficialStoreUrl,
  onBackupSourcesChange,
  onPreferredSourceChange,
}) => {
  const backupSources = parseSourcingBackupLinks(backupSourcesJson);

  const hasUsableBackupOfType = (backupSourceType: BackupSourceType): boolean =>
    backupSources.some((backupSource) => backupSource.source === backupSourceType && backupSource.url.trim() !== '');

  // Preferred source can only be a channel the product has a usable link for: the main cp_url or a Coupang
  // backup, a Naver backup, or any official store url (bookkeeping).
  const availablePreferredSources: string[] = [];
  if (Boolean(mainCoupangUrl && mainCoupangUrl.trim() !== '') || hasUsableBackupOfType(BackupSourceType.COUPANG)) {
    availablePreferredSources.push(BackupSourceType.COUPANG);
  }
  if (hasUsableBackupOfType(BackupSourceType.NAVER)) {
    availablePreferredSources.push(BackupSourceType.NAVER);
  }
  if (hasOfficialStoreUrl) {
    availablePreferredSources.push(OFFICIAL_STORE_PREFERRED_VALUE);
  }

  const preferredSourceOptions =
    preferredSource && !availablePreferredSources.includes(preferredSource) ? [...availablePreferredSources, preferredSource] : availablePreferredSources;

  const emitBackupSources = (updatedBackupSources: BackupSource[]) => {
    onBackupSourcesChange(serializeSourcingBackupLinks(updatedBackupSources));
  };

  const addBackupSource = () => {
    emitBackupSources([...backupSources, { source: BackupSourceType.COUPANG, url: '', status: null }]);
  };

  // Any edit to a link's source/url invalidates its last scrape, so reset status to not-scraped (null).
  const updateBackupSource = (backupIndex: number, partialBackupSource: Partial<BackupSource>) => {
    emitBackupSources(
      backupSources.map((backupSource, currentIndex) => (currentIndex === backupIndex ? { ...backupSource, ...partialBackupSource, status: null } : backupSource)),
    );
  };

  const removeBackupSource = (backupIndex: number) => {
    emitBackupSources(backupSources.filter((_, currentIndex) => currentIndex !== backupIndex));
  };

  return (
    <div className="col-md-12">
      <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600', display: 'block', marginBottom: '0.15rem' }}>Backup Sourcing Links</Form.Label>
      <span style={{ fontSize: '0.7rem', fontWeight: 400, color: '#888', display: 'block', marginBottom: '0.5rem' }}>Checked via scrape; Naver only supports brand.naver.com</span>

      {backupSources.map((backupSource, backupIndex) => (
        <div key={backupIndex} className="d-flex gap-2 mb-2 align-items-center">
          <Form.Select
            size="sm"
            style={{ maxWidth: '160px' }}
            value={backupSource.source}
            onChange={(event) => updateBackupSource(backupIndex, { source: event.target.value as BackupSourceType })}
          >
            {BACKUP_SOURCE_TYPES.map((backupType) => (
              <option key={backupType} value={backupType}>
                {SOURCE_LABELS[backupType]}
              </option>
            ))}
          </Form.Select>
          <Form.Control
            size="sm"
            type="url"
            placeholder="https://..."
            value={backupSource.url}
            onChange={(event) => updateBackupSource(backupIndex, { url: event.target.value })}
          />
          <div style={{ width: '24px', flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{renderBackupStatus(backupSource.status, backupIndex)}</div>
          <Button size="sm" variant="outline-danger" onClick={() => removeBackupSource(backupIndex)} title="Remove link">
            <Trash2 size={14} />
          </Button>
        </div>
      ))}

      <Button size="sm" variant="outline-secondary" onClick={addBackupSource}>
        <Plus size={14} /> Add backup link
      </Button>

      <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600', display: 'block', marginTop: '0.75rem' }}>Preferred Source</Form.Label>
      <Form.Select size="sm" value={preferredSource ?? ''} onChange={(event) => onPreferredSourceChange(event.target.value)}>
        <option value="">No preference (default)</option>
        {preferredSourceOptions.map((sourceValue) => (
          <option key={sourceValue} value={sourceValue}>
            {SOURCE_LABELS[sourceValue] ?? sourceValue}
            {availablePreferredSources.includes(sourceValue) ? '' : ' (no link)'}
          </option>
        ))}
      </Form.Select>
    </div>
  );
};
